import React, { useEffect, useState } from "react";
import { Shell, Section, KpiCard } from "@/components/loomi/Layout";
import { PANEL_NAV } from "./PanelDashboard";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Gift, Sparkles } from "lucide-react";

export default function PanelRewards() {
  const [me, setMe] = useState(null);
  const [rewards, setRewards] = useState([]);
  const [redemptions, setRedemptions] = useState([]);
  const [busy, setBusy] = useState(null);

  const load = async () => {
    const [m, r, red] = await Promise.all([
      api.get("/panel/me"),
      api.get("/rewards"),
      api.get("/panel/redemptions"),
    ]);
    setMe(m.data); setRewards(r.data); setRedemptions(red.data);
  };
  useEffect(() => { load(); }, []);

  const redeem = async (rid) => {
    setBusy(rid);
    try {
      await api.post("/panel/redeem", { reward_id: rid });
      toast.success("Redemption requested — pending approval");
      load();
    } catch (e) { toast.error("Redeem failed", { description: formatApiError(e) }); }
    finally { setBusy(null); }
  };

  const balance = me?.points_balance ?? 0;

  return (
    <Shell title="Rewards" portalLabel="Panel" nav={PANEL_NAV}>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        <KpiCard label="Your balance" value={`${balance} pts`} accent="green" testId="rewards-kpi-balance" />
        <KpiCard label="Available rewards" value={rewards.length} accent="primary" testId="rewards-kpi-available" />
        <KpiCard label="Pending redemptions" value={redemptions.filter(r => r.status === "pending").length} accent="amber" testId="rewards-kpi-pending" />
      </div>

      <div className="mt-8">
        <Section title="Catalog" testId="section-rewards-catalog">
          <div className="grid md:grid-cols-3 gap-4">
            {rewards.map((r) => {
              const canAfford = balance >= r.cost_points;
              return (
                <div key={r.id} className="glass rounded-xl p-5 border border-white/10" data-testid={`reward-card-${r.id}`}>
                  <div className="flex items-start justify-between mb-3">
                    <div className="w-10 h-10 rounded-lg bg-emerald-500/20 border border-emerald-400/30 flex items-center justify-center">
                      <Gift className="w-5 h-5 text-emerald-300" />
                    </div>
                    <div className="text-right">
                      <div className="text-[10px] uppercase tracking-widest text-mute">Cost</div>
                      <div className="font-display font-extrabold text-xl">{r.cost_points} pts</div>
                    </div>
                  </div>
                  <h4 className="font-display font-bold text-lg leading-snug">{r.name}</h4>
                  <p className="text-xs text-mute mt-1 mb-4">{r.description || " "}</p>
                  <button disabled={!canAfford || busy === r.id}
                    onClick={() => redeem(r.id)}
                    data-testid={`redeem-btn-${r.id}`}
                    className="w-full bg-indigo-500 hover:bg-indigo-400 disabled:opacity-40 disabled:cursor-not-allowed rounded-md py-2 text-sm font-medium">
                    {busy === r.id ? "Redeeming…" : canAfford ? "Redeem" : `Need ${r.cost_points - balance} more pts`}
                  </button>
                </div>
              );
            })}
          </div>
        </Section>
      </div>

      <div className="mt-6">
        <Section title="Your redemption history" testId="section-panel-redemptions">
          {redemptions.length === 0 ? (
            <div className="text-mute text-sm py-8 text-center" data-testid="panel-redemptions-empty">No redemptions yet.</div>
          ) : (
            <table className="w-full text-sm" data-testid="panel-redemptions-table">
              <thead>
                <tr className="text-left text-xs uppercase tracking-widest text-mute border-b border-white/10">
                  <th className="py-3 pr-4">Reward</th><th className="py-3 pr-4 text-right">Cost</th><th className="py-3 pr-4">Status</th><th className="py-3 pr-4">Date</th>
                </tr>
              </thead>
              <tbody>
                {redemptions.map((r) => (
                  <tr key={r.id} className="border-b border-white/5" data-testid={`panel-redemption-row-${r.id}`}>
                    <td className="py-3 pr-4 font-medium">{r.reward_name}</td>
                    <td className="py-3 pr-4 text-right font-mono">{r.cost_points}</td>
                    <td className="py-3 pr-4 uppercase text-xs tracking-widest">{r.status}</td>
                    <td className="py-3 pr-4 text-mute text-xs">{new Date(r.created_at).toLocaleDateString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Section>
      </div>
    </Shell>
  );
}
